// Lib
import { last } from 'lodash/fp';

// Utils
import { createShallowSelector } from '../../utils/milanoteReselect/milanoteReselect';
import { getBranchPublicPermissionProperties } from '../../../common/permissions/elementPermissionsUtil';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { isBoard } from '../../../common/elements/utils/elementTypeUtils';

// Selectors
import {
    getCurrentBoardAncestorsAndSelfSelector,
    getCurrentBoardId,
} from '../../element/selectors/currentBoardSelector';
import { getPublishPreviewStartingBoardId } from './previewSelector';

/**
 * Determines the root breadcrumb element ID for the board preview state.
 */
const getPublishedRootElementId = ({
    startingBoardId,
    currentBoardId,
    published,
    publishedElementIds,
    legacySecretLinkEnabled,
    legacySecretLinkIds,
}) => {
    if (published) return last(publishedElementIds);

    if (legacySecretLinkEnabled) return last(legacySecretLinkIds);

    return startingBoardId || currentBoardId;
};

const getBreadcrumbsForRoot = ({ breadcrumbRootElementId, ancestorElements }) => {
    const { breadcrumbIds } = ancestorElements.reduce(
        (acc, element) => {
            if (acc.complete) return acc;

            const elementId = getElementId(element);

            if (isBoard(element)) acc.breadcrumbIds.push(elementId);

            if (elementId === breadcrumbRootElementId) {
                acc.complete = true;
            }

            return acc;
        },
        { breadcrumbIds: [], complete: false },
    );

    return breadcrumbIds.reverse();
};

/**
 * Retrieves the breadcrumbs to the highest published board, or the starting board ID if there's
 * no published board.
 */
export const getPublishedBreadcrumbIds = createShallowSelector(
    getCurrentBoardAncestorsAndSelfSelector,
    getCurrentBoardId,
    getPublishPreviewStartingBoardId,
    (ancestorElements, currentBoardId, startingBoardId) => {
        const collapsedPermissionResult = getBranchPublicPermissionProperties(ancestorElements, currentBoardId);

        const breadcrumbRootElementId = getPublishedRootElementId({
            currentBoardId,
            startingBoardId,
            ...collapsedPermissionResult,
        });

        return getBreadcrumbsForRoot({ breadcrumbRootElementId, ancestorElements });
    },
);
